import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ClockIcon, TruckIcon, PhoneIcon, MapPinIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import AdminService from '../services/adminService';
const OrderDetails = () => {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const {
    user
  } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    // Check if user is logged in
    if (!user) {
      navigate('/login');
      return;
    }
    const loadOrder = async () => {
      try {
        // Load order from database
        const orderDetails = await AdminService.getOrderDetails(id);
        if (orderDetails) {
          // Format order data to match expected structure
          const formattedOrder = {
            id: orderDetails.OrderID,
            date: orderDetails.Order_Date,
            status: orderDetails.Order_Status,
            total: orderDetails.Total_Amount,
            subtotal: orderDetails.Total_Amount - 20,
            deliveryFee: 20,
            items: orderDetails.items.map((item: any) => ({
              name: item.name,
              quantity: item.Quantity,
              price: item.price
            })),
            deliveryAddress: user.Address || 'No address provided',
            contactNumber: user.Contact || 'No contact provided',
            paymentMethod: 'Cash on Delivery',
            specialInstructions: ''
          };
          setOrder(formattedOrder);
        } else {
          // If order not found, redirect to dashboard
          navigate(user.User_type === 'admin' ? '/admin' : '/customer');
        }
      } catch (error) {
        console.error('Error loading order:', error);
        navigate(user.User_type === 'admin' ? '/admin' : '/customer');
      } finally {
        setLoading(false);
      }
    };
    loadOrder();
    // Set up interval to refresh order every 5 seconds
    const intervalId = setInterval(loadOrder, 5000);
    return () => clearInterval(intervalId);
  }, [id, navigate, user]);
  const handleCancelOrder = async () => {
    if (window.confirm('Are you sure you want to cancel this order?')) {
      try {
        // Update order status in database
        await AdminService.updateOrderStatus(order.id, 'Cancelled');
        // Update local state
        setOrder({
          ...order,
          status: 'Cancelled'
        });
        alert('Order cancelled successfully');
      } catch (error) {
        console.error('Error cancelling order:', error);
        alert('Failed to cancel order. Please try again.');
      }
    }
  };
  if (loading) {
    return <div className="flex justify-center items-center min-h-screen bg-amber-50" data-id="element-1529">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-800" data-id="element-1530"></div>
      </div>;
  }
  if (!order) {
    return <div className="bg-amber-50 min-h-screen py-8 flex justify-center items-center" data-id="element-1531">
        <div className="max-w-md mx-auto px-4 text-center" data-id="element-1532">
          <h2 className="text-2xl font-bold text-amber-900 mb-4" data-id="element-1533">
            Order Not Found
          </h2>
          <p className="text-amber-800 mb-6" data-id="element-1534">
            We couldn't find the order you're looking for.
          </p>
          <Link to={user.User_type === 'admin' ? '/admin' : '/customer'} className="px-4 py-2 bg-amber-800 text-white rounded-md hover:bg-amber-900" data-id="element-1535">
            Return to Dashboard
          </Link>
        </div>
      </div>;
  }
  // Calculate the estimated delivery time (30-45 minutes from order time)
  const orderDate = new Date(order.date);
  const minDeliveryTime = new Date(orderDate.getTime() + 30 * 60000);
  const maxDeliveryTime = new Date(orderDate.getTime() + 45 * 60000);
  const formatTime = date => {
    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  // Helper function to get progress percentage
  const getProgressPercentage = status => {
    if (status === 'Delivered') return '100%';
    if (status === 'Delivering' || status === 'Out for Delivery') return '75%';
    if (status === 'Preparing') return '50%';
    if (status === 'Confirmed' || status === 'Pending') return '25%';
    return '10%';
  };
  // Helper function to check if status is active
  const isStatusActive = checkStatus => {
    const statusOrder = ['Confirmed', 'Pending', 'Preparing', 'Delivering', 'Out for Delivery', 'Delivered'];
    const currentIndex = statusOrder.indexOf(order.status);
    const checkIndex = statusOrder.indexOf(checkStatus);
    return currentIndex >= checkIndex && order.status !== 'Cancelled';
  };
  return <div className="bg-amber-50 min-h-screen py-8" data-id="element-1536">
      <div className="max-w-3xl mx-auto px-4" data-id="element-1537">
        <div className="bg-white rounded-lg shadow-md overflow-hidden" data-id="element-1538">
          <div className="p-6 bg-amber-50 border-b border-amber-200" data-id="element-1539">
            <div className="flex justify-between items-center" data-id="element-1540">
              <h1 className="text-2xl font-bold text-amber-900" data-id="element-1541">
                Order #{order.id}
              </h1>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${order.status === 'Delivered' ? 'bg-green-100 text-green-800' : order.status === 'Preparing' ? 'bg-yellow-100 text-yellow-800' : order.status === 'Cancelled' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`} data-id="element-1542">
                {order.status}
              </span>
            </div>
            <p className="text-amber-800 mt-1" data-id="element-1543">
              Placed on {new Date(order.date).toLocaleString()}
            </p>
          </div>
          <div className="p-6 space-y-6" data-id="element-1544">
            {/* Delivery Tracking */}
            <div data-id="element-1545">
              <h2 className="text-lg font-semibold text-amber-900 mb-3" data-id="element-1546">
                Delivery Status
              </h2>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-id="element-1547">
                <div className="flex items-center mb-3" data-id="element-1548">
                  <ClockIcon className="h-5 w-5 text-amber-800 mr-2" data-id="element-1549" />
                  <span className="font-medium" data-id="element-1550">
                    Estimated delivery: {formatTime(minDeliveryTime)} -{' '}
                    {formatTime(maxDeliveryTime)}
                  </span>
                </div>
                <div className="relative pt-1" data-id="element-1551">
                  <div className="flex mb-2 items-center justify-between" data-id="element-1552">
                    <div data-id="element-1553">
                      <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-amber-600 bg-amber-200" data-id="element-1554">
                        {getProgressPercentage(order.status)}
                      </span>
                    </div>
                  </div>
                  <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-amber-200" data-id="element-1555">
                    <div style={{
                    width: getProgressPercentage(order.status)
                  }} className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-amber-600" data-id="element-1556"></div>
                  </div>
                </div>
                <div className="grid grid-cols-4 text-xs text-center" data-id="element-1557">
                  <div className={`${order.status !== 'Cancelled' ? 'text-amber-800' : 'text-gray-400'}`} data-id="element-1558">
                    <div className={`rounded-full h-6 w-6 flex items-center justify-center mx-auto mb-1 ${order.status !== 'Cancelled' ? 'bg-amber-600 text-white' : 'bg-gray-300 text-gray-500'}`} data-id="element-1559">
                      1
                    </div>
                    <span data-id="element-1560">Confirmed</span>
                  </div>
                  <div className={`${isStatusActive('Preparing') ? 'text-amber-800' : 'text-gray-400'}`} data-id="element-1561">
                    <div className={`rounded-full h-6 w-6 flex items-center justify-center mx-auto mb-1 ${isStatusActive('Preparing') ? 'bg-amber-600 text-white' : 'bg-gray-300 text-gray-500'}`} data-id="element-1562">
                      2
                    </div>
                    <span data-id="element-1563">Preparing</span>
                  </div>
                  <div className={`${isStatusActive('Delivering') ? 'text-amber-800' : 'text-gray-400'}`} data-id="element-1564">
                    <div className={`rounded-full h-6 w-6 flex items-center justify-center mx-auto mb-1 ${isStatusActive('Delivering') ? 'bg-amber-600 text-white' : 'bg-gray-300 text-gray-500'}`} data-id="element-1565">
                      3
                    </div>
                    <span data-id="element-1566">On the way</span>
                  </div>
                  <div className={`${order.status === 'Delivered' ? 'text-amber-800' : 'text-gray-400'}`} data-id="element-1567">
                    <div className={`rounded-full h-6 w-6 flex items-center justify-center mx-auto mb-1 ${order.status === 'Delivered' ? 'bg-amber-600 text-white' : 'bg-gray-300 text-gray-500'}`} data-id="element-1568">
                      4
                    </div>
                    <span data-id="element-1569">Delivered</span>
                  </div>
                </div>
              </div>
            </div>
            {/* Delivery Information */}
            <div data-id="element-1570">
              <h2 className="text-lg font-semibold text-amber-900 mb-3" data-id="element-1571">
                Delivery Information
              </h2>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-1572">
                <div className="flex items-start mb-3" data-id="element-1573">
                  <MapPinIcon className="h-5 w-5 text-amber-800 mr-2 mt-0.5" data-id="element-1574" />
                  <div data-id="element-1575">
                    <span className="font-medium block" data-id="element-1576">Delivery Address:</span>
                    <span className="text-gray-700" data-id="element-1577">
                      {order.deliveryAddress}
                    </span>
                  </div>
                </div>
                <div className="flex items-center" data-id="element-1578">
                  <PhoneIcon className="h-5 w-5 text-amber-800 mr-2" data-id="element-1579" />
                  <div data-id="element-1580">
                    <span className="font-medium" data-id="element-1581">Contact Number:</span>
                    <span className="text-gray-700 ml-1" data-id="element-1582">
                      {order.contactNumber}
                    </span>
                  </div>
                </div>
                {order.specialInstructions && <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-1583">
                    <span className="font-medium block mb-1" data-id="element-1584">
                      Special Instructions:
                    </span>
                    <span className="text-gray-700" data-id="element-1585">
                      {order.specialInstructions}
                    </span>
                  </div>}
              </div>
            </div>
            {/* Order Items */}
            <div data-id="element-1586">
              <h2 className="text-lg font-semibold text-amber-900 mb-3" data-id="element-1587">
                Order Details
              </h2>
              <div className="border border-gray-200 rounded-md overflow-hidden" data-id="element-1588">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-1589">
                  <thead className="bg-gray-50" data-id="element-1590">
                    <tr data-id="element-1591">
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1592">
                        Item
                      </th>
                      <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1593">
                        Qty
                      </th>
                      <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1594">
                        Price
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-1595">
                    {order.items.map((item, index) => <tr key={index} data-id="element-1596">
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1597">
                          {item.name}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-1598">
                          {item.quantity}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right" data-id="element-1599">
                          ₱{(item.price * item.quantity).toFixed(2)}
                        </td>
                      </tr>)}
                  </tbody>
                  <tfoot className="bg-gray-50" data-id="element-1600">
                    <tr data-id="element-1601">
                      <td colSpan={2} className="px-4 py-2 text-right text-sm font-medium text-gray-500" data-id="element-1602">
                        Subtotal
                      </td>
                      <td className="px-4 py-2 text-right text-sm font-medium text-gray-900" data-id="element-1603">
                        ₱{order.subtotal.toFixed(2)}
                      </td>
                    </tr>
                    <tr data-id="element-1604">
                      <td colSpan={2} className="px-4 py-2 text-right text-sm font-medium text-gray-500" data-id="element-1605">
                        Delivery Fee
                      </td>
                      <td className="px-4 py-2 text-right text-sm font-medium text-gray-900" data-id="element-1606">
                        ₱{order.deliveryFee.toFixed(2)}
                      </td>
                    </tr>
                    <tr data-id="element-1607">
                      <td colSpan={2} className="px-4 py-2 text-right text-sm font-bold text-gray-900" data-id="element-1608">
                        Total
                      </td>
                      <td className="px-4 py-2 text-right text-sm font-bold text-gray-900" data-id="element-1609">
                        ₱{order.total.toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
            {/* Payment Information */}
            <div data-id="element-1610">
              <h2 className="text-lg font-semibold text-amber-900 mb-3" data-id="element-1611">
                Payment Information
              </h2>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-1612">
                <p className="text-gray-700" data-id="element-1613">
                  <span className="font-medium" data-id="element-1614">Method:</span>{' '}
                  {order.paymentMethod}
                </p>
                {order.paymentMethod === 'GCash' && <p className="text-gray-700 mt-2" data-id="element-1615">
                    <span className="font-medium" data-id="element-1616">Status:</span>{' '}
                    <span className="text-green-600 font-medium" data-id="element-1617">Paid</span>
                  </p>}
              </div>
            </div>
            {/* Actions */}
            <div className="border-t border-gray-200 pt-6 flex justify-between items-center" data-id="element-1618">
              <Link to={user.User_type === 'admin' ? '/admin' : '/customer'} className="px-4 py-2 border border-amber-800 text-amber-800 rounded-md hover:bg-amber-50" data-id="element-1619">
                Back to Dashboard
              </Link>
              {order.status !== 'Delivered' && order.status !== 'Cancelled' && <button className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700" onClick={handleCancelOrder} data-id="element-1620">
                  Cancel Order
                </button>}
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default OrderDetails;